import { useEffect, useMemo, useState } from 'react';
import { api, type ProfitMisuRow } from '../lib/api';
import { useSticky } from '../lib/sticky';
import { ErrorBox, money } from '../components/DataTable';
import { CalcNote } from '../components/CalcNote';

/**
 * 경영 분석 › 미수 — 시트 「미수해소」 3개년미수·담당자별·최근3개월 판별·미수 속도 대체(10-24).
 * 서버 `listProfitMisu`(`functions/src/profitOps.ts` MISU_SQL) — 최근 36개월 · 이번 달 제외 · 입금일 없는 청구만.
 * 통제 = 청구 뒤 지난 날 > 그 거래처 평균 입금일수 + 0.9. 피드백 = 미수 메모(미수금 화면에서 고친다).
 */

type Pick = 'all' | 'ctl' | 'gone';
const errMsg = (e: unknown) => (e instanceof Error ? e.message : String(e));
const isCtl = (r: ProfitMisuRow) => r.days > Number(r.avg_days);

export default function ProfitMisu() {
  const [rows, setRows] = useState<ProfitMisuRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [by, setBy] = useSticky<'list' | 'manager'>('profit.misu.by', 'manager');
  const [pick, setPick] = useState<Pick>('all');
  const [manager, setManager] = useState<string | null>(null);
  const [q, setQ] = useState('');

  useEffect(() => {
    api.listProfitMisu({}).then((r) => setRows(r.rows)).catch((e) => setError(errMsg(e)));
  }, []);

  const managers = useMemo(() => {
    const m = new Map<string, { name: string; n: number; amt: number; ctl: number; ctlAmt: number; gone: boolean }>();
    for (const r of rows ?? []) {
      const k = r.manager || '(담당 없음)';
      const o = m.get(k) ?? { name: k, n: 0, amt: 0, ctl: 0, ctlAmt: 0, gone: !r.manager_active };
      o.n++; o.amt += Number(r.amt);
      if (isCtl(r)) { o.ctl++; o.ctlAmt += Number(r.amt); }
      m.set(k, o);
    }
    return [...m.values()].sort((a, b) => b.ctlAmt - a.ctlAmt || b.amt - a.amt);
  }, [rows]);
  const shown = useMemo(() => {
    const w = q.trim();
    return (rows ?? []).filter((r) => (!manager || (r.manager || '(담당 없음)') === manager)
      && (pick !== 'ctl' || isCtl(r)) && (pick !== 'gone' || !r.manager_active)
      && (!w || r.cust_name.includes(w) || r.manager.includes(w) || String(r.recv_id) === w))
      .sort((a, b) => b.days - a.days);
  }, [rows, manager, pick, q]);
  const tot = useMemo(() => ({
    n: rows?.length ?? 0, amt: (rows ?? []).reduce((a, r) => a + Number(r.amt), 0),
    ctl: (rows ?? []).filter(isCtl).length, gone: (rows ?? []).filter((r) => !r.manager_active).length,
  }), [rows]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1">
          {([['manager', '버스인접수담당자별'], ['list', '전체 목록']] as const).map(([k, l]) => (
            <button key={k} className={by === k ? 'btn-primary' : 'btn'} onClick={() => { setBy(k); setManager(null); }}>{l}</button>
          ))}
        </div>
        {by === 'list' && (
          <>
            <div className="flex gap-1 text-sm">
              {([['all', `전체 ${tot.n}`], ['ctl', `통제 ${tot.ctl}`], ['gone', `퇴사 담당 ${tot.gone}`]] as const).map(([k, l]) => (
                <button key={k} className={pick === k ? 'btn-primary' : 'btn'} onClick={() => setPick(k)}>{l}</button>
              ))}
            </div>
            <input className="field w-48" placeholder="거래처·버스인접수담당자·접수번호" value={q} onChange={(e) => setQ(e.target.value)} />
            {manager && <button className="btn-ghost text-xs" onClick={() => setManager(null)}>담당 「{manager}」 풀기 ✕</button>}
          </>
        )}
        <CalcNote>입금일이 비어 있는 청구(최근 36개월 · 이번 달 제외)입니다 — 지금 원장 그대로라 입금이 들어오면 바로 빠집니다.
        회계팀 메모는 정산 › 미수금에서 고칩니다. 접수 번호를 누르면 그 달 거래처 정산이 열립니다.</CalcNote>
        <span className="ml-auto text-xs text-ink-soft">최근 36개월 미수 {tot.n}건 · {money(tot.amt)} · 통제 {tot.ctl}건</span>
      </div>
      {error && <ErrorBox message={error} />}
      {!rows && !error && <div className="py-8 text-center text-sm text-ink-soft">세는 중…</div>}
      {rows && by === 'manager' && (
        <div className="card max-h-[calc(100vh-12rem)] overflow-auto">
          <table className="w-full min-w-[40rem] text-sm tabular-nums">
            <thead className="sticky top-0 z-10 text-xs text-ink-soft [&_th]:bg-white">
              <tr className="border-b border-line">
                <th className="px-2 py-1.5 text-left">버스인접수담당자</th><th className="px-2 py-1.5 text-right">미수 건</th><th className="px-2 py-1.5 text-right">미수액</th>
                <th className="px-2 py-1.5 text-right" title="청구 뒤 지난 날이 그 거래처 평균 입금일수를 넘은 건">통제 건</th><th className="px-2 py-1.5 text-right">통제 금액</th>
              </tr>
            </thead>
            <tbody>
              {managers.map((m) => (
                <tr key={m.name} className="cursor-pointer border-b border-line/60 hover:bg-surface-soft" onClick={() => { setManager(m.name); setBy('list'); setPick('all'); }}>
                  <td className="px-2 py-1">{m.name}{m.gone && <span className="ml-1 rounded bg-slate-100 px-1 text-xs text-ink-soft">퇴사</span>}</td>
                  <td className="px-2 py-1 text-right">{m.n}</td><td className="px-2 py-1 text-right">{money(m.amt)}</td>
                  <td className={`px-2 py-1 text-right ${m.ctl ? 'text-red-700' : 'text-ink-faint'}`}>{m.ctl}</td>
                  <td className={`px-2 py-1 text-right ${m.ctl ? 'text-red-700' : 'text-ink-faint'}`}>{money(m.ctlAmt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {rows && by === 'list' && (
        <div className="card max-h-[calc(100vh-12rem)] overflow-auto">
          <table className="w-full min-w-[64rem] text-sm tabular-nums">
            <thead className="sticky top-0 z-10 text-xs text-ink-soft [&_th]:bg-white">
              <tr className="border-b border-line">
                {['정산월', '접수', '구분', '거래처', '버스인접수담당자', '청구상태', '청구일'].map((h) => <th key={h} className="px-2 py-1.5 text-left">{h}</th>)}
                <th className="px-2 py-1.5 text-right">미수액</th>
                <th className="px-2 py-1.5 text-right" title="오늘 − 청구일(없으면 정산월 말일)">지난 날</th>
                <th className="px-2 py-1.5 text-right" title="그 거래처 지난 입금의 평균 입금일수 + 0.9(시트와 같은 기준)">평균 입금</th>
                <th className="px-2 py-1.5 text-left">판별</th>
                <th className="px-2 py-1.5 text-left">회계팀 메모</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => (
                <tr key={`${r.recv_id}|${r.drive_mon}`} className="border-b border-line/60 hover:bg-surface-soft">
                  <td className="whitespace-nowrap px-2 py-1">{r.drive_mon}</td>
                  <td className="px-2 py-1"><a className="text-brand-700 hover:underline" href={`/settle?tab=cust&recv=${r.recv_id}&mon=${r.drive_mon}`}>{r.recv_id}</a></td>
                  <td className="px-2 py-1 text-ink-soft">{r.recv_type}</td>
                  <td className="px-2 py-1">{r.cust_name}</td>
                  <td className="whitespace-nowrap px-2 py-1 text-ink-soft">{r.manager || '—'}{!r.manager_active && ' (퇴사)'}</td>
                  <td className="whitespace-nowrap px-2 py-1">{r.req_status || '—'}</td>
                  <td className="whitespace-nowrap px-2 py-1 text-ink-soft">{r.req_date ?? '—'}</td>
                  <td className="px-2 py-1 text-right">{money(Number(r.amt))}</td>
                  <td className="px-2 py-1 text-right">{r.days}일</td>
                  <td className="px-2 py-1 text-right text-ink-soft">{r.avg_days}일</td>
                  <td className="px-2 py-1">{isCtl(r) ? <span className="rounded bg-red-50 px-1 text-xs text-red-700">통제</span> : <span className="text-xs text-ink-faint">PASS</span>}</td>
                  <td className="max-w-[18rem] truncate px-2 py-1 text-xs text-ink-soft" title={r.comment}>{r.comment}</td>
                </tr>
              ))}
              {shown.length === 0 && <tr><td colSpan={12} className="py-6 text-center text-ink-soft">해당하는 미수가 없습니다.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
